import { Component, Inject, OnInit, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CatchService } from 'src/app/services/reporting/catch.service';
import { SharedMethodsService } from 'src/app/services/shared-methods.service';
import { SpeciesData } from '../../data-models/catch-table-models/species-data.model';
import { TableRules } from '../../data-models/catch-table-models/table-rules.model';
import { speciesValidator } from '../../data-models/validator.model';

@Component({
    selector: 'app-add-species-dialog',
    templateUrl: './add-species-dialog.component.html',
    styleUrls: ['./add-species-dialog.component.css'],
    standalone: false
})

export class AddSpeciesDialogComponent implements OnInit {

  @ViewChild('keptInput', { static: false }) keptInputElement?: ElementRef;
  @ViewChild('speciesInput', { static: false }) speciesInputElement?: ElementRef;

  // used to determine if the species name can be edited
  speciesIsReadOnly: boolean = false;

  // species lists
  stringList: string[] = []; // subset of "list" - has all species names (used only for validation)
  speciesList: string[] = [];
  filteredSpecies: string[] = []; // the list of species names the user can pick from

  // dialog title
  displayedTitle: string = '';

  // extracting data from data inject
  identifier: string = '';
  currentFishList: SpeciesData[] = [];
  filteredFishList: SpeciesData[] = [];
  tableInfo: TableRules = new TableRules();

  // species details
  speciesForm: UntypedFormGroup = new UntypedFormGroup({
    speciesName: new FormControl<string>('', [Validators.required, speciesValidator(this.stringList)]),
    kept: new FormControl<string>(''),
    released: new FormControl<string>(''),
    offloadWeight: new FormControl<string>(''),
    offloadPieces: new FormControl<string>('')
  })

  constructor(
    private dialogRef: MatDialogRef<AddSpeciesDialogComponent>,
    public sharedMethodsService: SharedMethodsService,
    public catchService: CatchService,
    @Inject(MAT_DIALOG_DATA) private data: { identifier: string, species: SpeciesData, currentFishList: SpeciesData[], 
      filteredFishList: SpeciesData[], tableInfo: TableRules }
  ) {

  }


  /*  ---------- Initialization Methods  ---------- */


  ngOnInit(): void {

    // if editing a species, autofill form with species details
    if (this.data != null) {
      this.populateForm();
    }
    this.fillSpecies(); // fill the species list available to the user
    this.setDialogConfig(); // set the dialog configuration
    this.onSpeciesChange(); // watch for a change in species selection

  }

  // Used to move the cursor into the second input field (kept) when in edit mode
  ngAfterViewInit(): void {

    if (this.speciesIsReadOnly) {
      setTimeout(() => {
      // Prevent any automatic focusing on speciesInput
      this.speciesInputElement?.nativeElement.setAttribute('tabindex', '-1');
      this.speciesInputElement?.nativeElement.blur();

      // Focus on the 'kept' input field
      this.keptInputElement?.nativeElement.focus();
      });
    }

  }

  // populate data needed for the dialog form
  populateForm(): void {

    this.identifier = this.data.identifier;
    this.currentFishList = this.data.currentFishList;
    this.filteredFishList = this.data.filteredFishList;
    this.tableInfo = this.data.tableInfo;

    if (this.data.species != null) { // patch form values when editing a species
      let speciesData: SpeciesData = this.data.species;
      this.speciesForm.patchValue({
        speciesName: speciesData.speciesName,
        kept: speciesData.kept,
        released: speciesData.released,
        offloadWeight: speciesData.offloadWeight,
        offloadPieces: speciesData.offloadPieces
      });
    }

  }

  // set the title of the dialog and read/write permissions of inputs
  setDialogConfig(): void {

     // set config based on whether the species is being added or edited
     switch (this.identifier) {
      case 'edit':
        this.displayedTitle = "Edit Species";
        this.speciesIsReadOnly = true;
        break;
      case 'add':
         this.displayedTitle = "Add Species";
         break;
    }

  }

  // fill up the species list based on what is not already in the table
  fillSpecies(): void { // list of species gets passed in here

    let currentSpeciesList: string[] = [];

    for(let species of this.currentFishList) { // get all species names that are already in the table
      currentSpeciesList.push(species.speciesName);
    } 
    for (let species of this.filteredFishList) {
      // only add the name to the list if it is not already in the table 
      if(!currentSpeciesList.includes(species.speciesName)) {
        this.speciesList.push(species.speciesName); 
      }
      this.stringList.push(species.speciesName); // fill the string list regardless
    }

    this.filteredSpecies = this.speciesList.filter(sl => sl != ''); // fill the filtered list
    this.speciesList.sort(); //sort species list alphabetically

  }

   // change the filtered species list whenever the input field changes
   onSpeciesChange(): void {

    this.speciesForm.get('speciesName')!.valueChanges.subscribe((speciesChange: string) => {
      this.filteredSpecies = this.speciesList.filter(sl => sl != '' && sl.toLowerCase().includes(speciesChange.toLowerCase()));
    });

  }


  /*  ---------- Add & Delete Species  ---------- */


  // Trigger addSpecies() when Enter is pressed
  onEnter(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;

    if (keyboardEvent.key === 'Enter') {
      keyboardEvent.preventDefault(); // Prevent the default form submission
      this.addSpecies();
    }
  }

  // add new species details
  addSpecies(): void {

    if (this.speciesForm.valid && this.checkSpecies(this.speciesForm.get('speciesName')!.value)) {
      this.clearInputs(); 
      this.dialogRef.close(this.speciesForm.value);
    } else {
      this.speciesForm.markAllAsTouched();
    }

  }

  // delete current species details
  deleteSpecies(): void {

    this.dialogRef.close('delete');
    
  }

  
  /*  ---------- Helper Methods  ---------- */


  // check if entered species is a valid species
  checkSpecies(name: string): boolean {

    for (let species of this.filteredFishList) {
      if (species.speciesName.toLowerCase().includes(name.toLowerCase())) {
        this.speciesForm.patchValue({
          speciesName: species.speciesName,
        });
        return true;
      }
    }
    return false;

  }

  getCurrentFormSpecies(): string {

    return this.speciesForm.get('speciesName')?.value;

  }

  // clear any possible dead form inputs
  clearInputs(): void {

    if(this.catchService.isReleaseOnly(this.getCurrentFormSpecies(), this.tableInfo)){ // if species is release-only, clear any possible previous 'kept' input
      this.speciesForm.controls['kept'].reset(); // reset form value
    }

  }

}
